import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Outlet, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { publicApi } from "@/lib/api";
import Splash from "@/components/Splash";
import ZeroBSPledge, { shouldShowPledge, markPledgeAccepted } from "@/components/ZeroBSPledge";
import BottomTabBar from "@/components/BottomTabBar";
import AuthCallback from "@/components/AuthCallback";
import Feed from "@/pages/Feed";
import SearchPage from "@/pages/Search";
import Saved from "@/pages/Saved";
import Gazette, { GAZETTE_LAST_SEEN_KEY } from "@/pages/Gazette";
import ZeroHour from "@/pages/ZeroHour";
import SettingsPage from "@/pages/Settings";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCardEditor from "@/pages/AdminCardEditor";
import AdminGazetteEditor from "@/pages/AdminGazetteEditor";
import { Settings as SettingsIcon } from "lucide-react";
import StreakPill from "@/components/StreakPill";
import OwnerOnly from "@/components/OwnerOnly";

function useGazetteBadge() {
  const [unread, setUnread] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const d = await publicApi.gazette();
        if (cancelled) return;
        const seen = localStorage.getItem(GAZETTE_LAST_SEEN_KEY);
        if (!d.latest_published_at) { setUnread(false); return; }
        if (!seen) { setUnread(true); return; }
        setUnread(new Date(d.latest_published_at).getTime() > new Date(seen).getTime());
      } catch { /* ignore */ }
    };
    check();
    const iv = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  return unread;
}

function AppLayout() {
  const location = useLocation();
  const gazetteUnread = useGazetteBadge();
  const isAdmin = location.pathname.startsWith("/studio");
  return (
    <div className="min-h-screen bg-[#111622]">
      {!isAdmin && (
        <header className="fixed top-0 left-0 right-0 z-40 bg-[#111622]">
          <div className="mx-auto w-full max-w-[760px] px-4 sm:px-6 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5" aria-label="DISTILL home">
              <picture>
                <source srcSet="/brand/distill-mark.webp" type="image/webp" />
                <img
                  src="/brand/distill-mark.png"
                  srcSet="/brand/distill-mark.png 1x, /brand/distill-mark@2x.png 2x"
                  alt=""
                  width={36}
                  height={36}
                  className="h-9 w-9 rounded-lg object-cover"
                  draggable="false"
                  loading="eager"
                  decoding="async"
                  data-testid="header-logo"
                />
              </picture>
              <span className="flex flex-col leading-none">
                <span className="text-sm font-semibold tracking-[0.22em] text-[#F8FAFC]">DISTILL</span>
                <span className="mt-1 text-[10px] font-semibold text-[#94A3B8] tracking-wide">No BS, only GS.</span>
              </span>
            </Link>
            <div className="flex items-center gap-1.5">
              <StreakPill />
              <Link to="/settings" aria-label="Settings" data-testid="header-settings-link" className="h-10 w-10 rounded-xl inline-flex items-center justify-center text-[#94A3B8] hover:text-[#E85A2A] hover:bg-[#1A2232] transition-colors">
                <SettingsIcon className="h-5 w-5" aria-hidden />
              </Link>
            </div>
          </div>
        </header>
      )}
      <div className={isAdmin ? "" : "pt-14"}>
        <Outlet />
      </div>
      {!isAdmin && <BottomTabBar gazetteUnread={gazetteUnread} />}
    </div>
  );
}

function FirstRun({ suppress = false }) {
  const [showSplash, setShowSplash] = useState(true);
  const [showPledge, setShowPledge] = useState(false);
  useEffect(() => {
    // After splash duration, show pledge if not accepted yet
    if (!showSplash && !suppress) {
      if (shouldShowPledge()) setShowPledge(true);
    }
  }, [showSplash, suppress]);
  if (suppress) return null;
  return (
    <>
      {showSplash && <Splash onDone={() => setShowSplash(false)} />}
      <ZeroBSPledge
        open={showPledge}
        onAccept={() => { markPledgeAccepted(); setShowPledge(false); }}
        onClose={() => setShowPledge(false)}
      />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronous session_id detection to prevent race conditions
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdminRoute = location.pathname.startsWith("/studio");
  return (
    <>
      <FirstRun suppress={isAdminRoute} />
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Feed />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/gazette" element={<Gazette />} />
          <Route path="/zero-hour" element={<ZeroHour />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/studio/login" element={<AdminLogin />} />
          <Route path="/studio" element={<OwnerOnly><AdminDashboard /></OwnerOnly>} />
          <Route path="/studio/cards/new" element={<OwnerOnly><AdminCardEditor /></OwnerOnly>} />
          <Route path="/studio/cards/:id" element={<OwnerOnly><AdminCardEditor /></OwnerOnly>} />
          <Route path="/studio/gazette/new" element={<OwnerOnly><AdminGazetteEditor /></OwnerOnly>} />
          <Route path="/studio/gazette/:id" element={<OwnerOnly><AdminGazetteEditor /></OwnerOnly>} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
